/**
 * Login Page
 * ==========
 * Extracted from frontend/auth/login.blade.php
 *
 * Handles:
 * - Email verification modal (showEmailVerificationModal)
 * - User not found modal (showUserNotExistsModal)
 * - Password incorrect modal (showPasswordIncorrectModal)
 * - Resend activation email (resendActivationEmail)
 * - Send password reset email (sendPasswordResetEmail)
 * - Email validation helper (isValidEmail)
 *
 * All fetch calls migrated to axios.
 *
 * Blade→JS bridge: <script type="application/json" id="login-config">
 */

import axios from 'axios';
import { Swal } from '../../sweetalert-config.js';

function initLoginPage() {
    const configEl = document.getElementById('login-config');
    if (!configEl) return; // Not on login page

    let config;
    try {
        config = JSON.parse(configEl.textContent);
    } catch (e) {
        console.error('❌ Error parsing login-config:', e);
        return;
    }

    const { locale, errors, errorData, routes } = config;
    const isEnglish = locale === 'en';

    // =============================================
    // EMAIL VALIDATION HELPER
    // =============================================

    function isValidEmail(email) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    }

    // =============================================
    // RESEND ACTIVATION EMAIL
    // =============================================

    function resendActivationEmail(email) {
        Swal.fire({
            title: isEnglish ? 'Sending...' : 'Enviando...',
            text: isEnglish ? 'Please wait while we send your verification email.' : 'Por favor espera mientras enviamos tu email de verificación.',
            icon: 'info',
            allowOutsideClick: false,
            allowEscapeKey: false,
            allowEnterKey: false,
            showConfirmButton: false,
            didOpen: function() {
                Swal.showLoading();
            }
        });

        axios.post(routes.resendActivation, { email: email }, {
            headers: { 'Accept': 'application/json' }
        })
        .then(function(response) {
            var data = response.data;
            if (data.success) {
                Swal.fire({
                    title: isEnglish ? 'Email Sent!' : '¡Email Enviado!',
                    text: data.message,
                    icon: 'success',
                    confirmButtonText: 'OK',
                    confirmButtonColor: '#28a745'
                });
            } else {
                Swal.fire({
                    title: 'Error',
                    text: data.message,
                    icon: 'error',
                    confirmButtonText: 'OK',
                    confirmButtonColor: '#dc3545'
                });
            }
        })
        .catch(function(error) {
            console.error('Error:', error);
            Swal.fire({
                title: 'Error',
                text: isEnglish ? 'There was an error sending the email. Please try again.' : 'Hubo un error al enviar el email. Por favor intenta de nuevo.',
                icon: 'error',
                confirmButtonText: 'OK',
                confirmButtonColor: '#dc3545'
            });
        });
    }

    // =============================================
    // SEND PASSWORD RESET EMAIL
    // =============================================

    function sendPasswordResetEmail(email) {
        Swal.fire({
            title: isEnglish ? 'Sending...' : 'Enviando...',
            text: isEnglish ? 'Please wait while we send your password reset email.' : 'Por favor espera mientras enviamos tu email de restablecimiento.',
            icon: 'info',
            allowOutsideClick: false,
            allowEscapeKey: false,
            allowEnterKey: false,
            showConfirmButton: false,
            didOpen: function() {
                Swal.showLoading();
            }
        });

        axios.post(routes.sendPasswordReset, { email: email }, {
            headers: { 'Accept': 'application/json' }
        })
        .then(function(response) {
            var data = response.data;
            if (data.success) {
                Swal.fire({
                    title: isEnglish ? 'Email Sent!' : '¡Email Enviado!',
                    text: data.message,
                    icon: 'success',
                    confirmButtonText: 'OK',
                    confirmButtonColor: '#28a745'
                });
            } else {
                Swal.fire({
                    title: 'Error',
                    text: data.message,
                    icon: 'error',
                    confirmButtonText: 'OK',
                    confirmButtonColor: '#dc3545'
                });
            }
        })
        .catch(function(error) {
            console.error('Error:', error);
            Swal.fire({
                title: 'Error',
                text: isEnglish ? 'There was an error sending the email. Please try again.' : 'Hubo un error al enviar el email. Por favor intenta de nuevo.',
                icon: 'error',
                confirmButtonText: 'OK',
                confirmButtonColor: '#dc3545'
            });
        });
    }

    // =============================================
    // MODAL: EMAIL VERIFICATION REQUIRED
    // =============================================

    function showEmailVerificationModal(userEmail, userName) {
        Swal.fire({
            title: isEnglish ? 'Email Verification Required' : 'Verificación de Email Requerida',
            html: '<div class="login-modal-body">'
                + '<p><strong>' + (isEnglish ? 'Hi' : 'Hola') + ' ' + userName + ',</strong></p>'
                + '<p>' + (isEnglish
                    ? 'You need to verify your email address to activate your account and access the system.'
                    : 'Necesitas verificar tu dirección de correo electrónico para activar tu cuenta y acceder al sistema.')
                + '</p>'
                + '<p>' + (isEnglish
                    ? 'Enter your email below to receive a new verification link:'
                    : 'Ingresa tu correo a continuación para recibir un nuevo enlace de verificación:')
                + '</p>'
                + '<input type="email" id="resendEmail" class="swal2-input" placeholder="' + (isEnglish ? 'Your email address' : 'Tu dirección de correo') + '" value="' + userEmail + '">'
                + '<div class="login-modal-note">'
                + '<p>'
                + '<strong>' + (isEnglish ? 'Note:' : 'Nota:') + '</strong> '
                + (isEnglish
                    ? 'If verification emails are being blocked, you can contact'
                    : 'Si los correos de verificación están siendo bloqueados, puedes contactar a')
                + ' <a href="mailto:asanchez@oasishoteles.com">asanchez@oasishoteles.com</a> '
                + (isEnglish
                    ? 'to manually activate your account.'
                    : 'para activar manualmente tu cuenta.')
                + '</p>'
                + '</div>'
                + '</div>',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: isEnglish ? 'Send Verification Email' : 'Enviar Email de Verificación',
            cancelButtonText: isEnglish ? 'Cancel' : 'Cancelar',
            confirmButtonColor: '#7c6a55',
            cancelButtonColor: '#6c757d',
            width: '600px',
            preConfirm: function() {
                var email = document.getElementById('resendEmail').value;
                if (!email) {
                    Swal.showValidationMessage(isEnglish ? 'Please enter your email' : 'Por favor ingresa tu correo');
                    return false;
                }
                if (!isValidEmail(email)) {
                    Swal.showValidationMessage(isEnglish ? 'Please enter a valid email' : 'Por favor ingresa un correo válido');
                    return false;
                }
                return email;
            }
        }).then(function(result) {
            if (result.isConfirmed) {
                resendActivationEmail(result.value);
            }
        });
    }

    // =============================================
    // MODAL: USER NOT FOUND
    // =============================================

    function showUserNotExistsModal(attemptedEmail) {
        Swal.fire({
            title: isEnglish ? 'Account Not Found' : 'Cuenta No Encontrada',
            html: '<div class="login-modal-body">'
                + '<p>' + (isEnglish
                    ? 'No account was found with the email address:'
                    : 'No se encontró ninguna cuenta con el correo electrónico:')
                + '</p>'
                + '<p class="login-modal-email">' + attemptedEmail + '</p>'
                + '<p>' + (isEnglish
                    ? 'If you are not registered yet, please click the link below to create your account:'
                    : 'Si aún no está registrado, favor de hacer click en el enlace de abajo para crear su cuenta:')
                + '</p>'
                + '</div>',
            icon: 'info',
            showCancelButton: true,
            confirmButtonText: isEnglish ? 'Create Account' : 'Crear Cuenta',
            cancelButtonText: isEnglish ? 'Cancel' : 'Cancelar',
            confirmButtonColor: '#7c6a55',
            cancelButtonColor: '#6c757d',
            width: '500px'
        }).then(function(result) {
            if (result.isConfirmed) {
                window.location.href = routes.register;
            }
        });
    }

    // =============================================
    // MODAL: PASSWORD INCORRECT
    // =============================================

    function showPasswordIncorrectModal(userEmail, userName) {
        Swal.fire({
            title: isEnglish ? 'Incorrect Password' : 'Contraseña Incorrecta',
            html: '<div class="login-modal-body">'
                + '<p><strong>' + (isEnglish ? 'Hi' : 'Hola') + ' ' + userName + ',</strong></p>'
                + '<p>' + (isEnglish
                    ? 'The password you entered is incorrect.'
                    : 'La contraseña que ingresaste es incorrecta.')
                + '</p>'
                + '<p>' + (isEnglish
                    ? 'Would you like us to send you a password reset email?'
                    : '¿Te gustaría que te enviemos un correo para restablecer tu contraseña?')
                + '</p>'
                + '<div class="login-modal-info">'
                + '<p>'
                + '<strong><i class="fas fa-envelope"></i> ' + userEmail + '</strong><br>'
                + (isEnglish
                    ? 'We will send the reset link to this email address.'
                    : 'Enviaremos el enlace de restablecimiento a esta dirección de correo.')
                + '</p>'
                + '</div>'
                + '</div>',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: isEnglish ? 'Send Reset Email' : 'Enviar Email de Restablecimiento',
            cancelButtonText: isEnglish ? 'Cancel' : 'Cancelar',
            confirmButtonColor: '#7c6a55',
            cancelButtonColor: '#6c757d',
            width: '550px'
        }).then(function(result) {
            if (result.isConfirmed) {
                sendPasswordResetEmail(userEmail);
            }
        });
    }

    // =============================================
    // AUTO-TRIGGER MODALS BASED ON ERROR FLAGS
    // =============================================

    if (errors.user_not_exists && errorData.attempted_email) {
        showUserNotExistsModal(errorData.attempted_email);
    } else if (errors.password_incorrect && errorData.user_email) {
        showPasswordIncorrectModal(errorData.user_email, errorData.user_name || '');
    } else if (errors.email_not_verified && errorData.user_email) {
        showEmailVerificationModal(errorData.user_email, errorData.user_name || '');
    }

    console.log('🔐 Login page inicializada');
}

document.addEventListener('DOMContentLoaded', initLoginPage);
